import { DatePipe } from '@angular/common';
import { Component, OnDestroy, computed, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, ParamMap, Router, RouterModule } from '@angular/router';
import { IconPillComponent, ImageUrlPipe, PaginationModule } from '@frontend/shared';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TranslocoModule, provideTranslocoScope } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { reloadOnLocaleChange } from '../../language';
import { LoaderGlobeAnimationDetailComponent, LoadingObject, LoadingService } from '../../loaders';
import {
  DEFAULT_STAGES,
  FEATURE_REQUEST_STAGES,
  featureRequestStageBadge,
  featureRequestStageText,
  featureRequestStatusKey,
  FeatureRequestError,
  FeatureRequestPage,
  FeatureRequestQuery,
  FeatureRequestStage,
  FeatureRequestTag,
  FeatureRequestThumbnail,
  TAG_PILL_LIMIT,
} from '../feature-request.model';
import { MediaService } from '../../content/media/media.service';
import { FeatureRequestService } from '../feature-request.service';
import { ideasBaseRoute, ideasHubSlug, nameHubInBreadcrumb } from '../ideas-context';

const PAGE_SIZE = 20;

type ListState = { stages: FeatureRequestStage[]; tags: string[]; mine: boolean; following: boolean; page: number; search: string };

const STAGE_KEYS = FEATURE_REQUEST_STAGES.map((stage) => stage.key);

/**
 * The Ideas list: in the public area, every request the reader may see; in a
 * hub's view, that hub's private requests, the reader's own pending ones
 * among them. Stages, tags, "mine", "following", search and page live in the URL.
 *
 * **One wrapping toolbar above the list**, as on CultureConnector's Ideas tab:
 * search, the three stages, "only mine" and, past {@link TAG_PILL_LIMIT} tags,
 * the tags as a dropdown checklist. Up to that many, the tags are pills on a line
 * of their own. Stages and tags are both "any of", and look different: stages
 * are filled pills whose statuses the backend defines (`filter[stage]`), Pending
 * and In progress on at first; tags are round icon pills with a tag mark, offered
 * from the list's `meta.tags` with counts — the Ideas plan, decisions 3.26 and
 * 3.27. Each row still shows its precise status.
 *
 * The list is plain — name, status, summary and the relevance line — on
 * purpose: the Ideas plan's decision 3.17 — *The list is plain; the relevance
 * line is what makes it engaging*.
 */
@Component({
  selector: 'multisite-ideas-list',
  templateUrl: './ideas-list.component.html',
  styleUrl: './ideas-list.component.scss',
  imports: [DatePipe, FormsModule, RouterModule, TranslocoModule, PaginationModule, BsDropdownModule, IconPillComponent, ImageUrlPipe, LoaderGlobeAnimationDetailComponent],
  providers: [provideTranslocoScope('ideas')],
})
export class IdeasListComponent implements OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private ideas = inject(FeatureRequestService);
  private loadingService = inject(LoadingService);

  readonly hub = ideasHubSlug(this.route);
  private readonly nameHub = nameHubInBreadcrumb(this.hub);
  readonly baseRoute = ideasBaseRoute(this.hub);
  readonly stages = FEATURE_REQUEST_STAGES;
  readonly statusKey = featureRequestStatusKey;
  readonly stageBadge = featureRequestStageBadge;
  readonly stageText = featureRequestStageText;

  /**
   * One fixed size for every listing picture: Cloudinary bills each distinct transformation of
   * each image, so the width never follows the viewport — and never `f_auto` or `q_auto`.
   */
  readonly thumbnailTransform = 'w_240,c_fill,ar_16:9';

  /**
   * Flags are static assets held in the production cloud only, so they are drawn from
   * `MediaService`'s fixed base, as every other flag on the site is — not through
   * `ImageUrlService`, whose cloud is the environment's own.
   */
  readonly flagBaseUrl = inject(MediaService).cloudinary_base_url;

  // The template's narrowing of the thumbnail's kinds.
  readonly asImage = (thumbnail: FeatureRequestThumbnail) => (thumbnail.kind === 'image' ? thumbnail : null);
  readonly asIcon = (thumbnail: FeatureRequestThumbnail) => (thumbnail.kind === 'icon' ? thumbnail : null);
  readonly asFlag = (thumbnail: FeatureRequestThumbnail) => (thumbnail.kind === 'flag' ? thumbnail : null);

  readonly state = toSignal(this.route.queryParamMap.pipe(map((params) => IdeasListComponent.read(params))), { requireSync: true });

  page = signal<FeatureRequestPage | null>(null);
  failure = signal<FeatureRequestError | null>(null);
  searchText = '';

  loadingObject = signal<LoadingObject>({});
  isAnythingLoading = computed(() => this.loadingService.isAnythingLoading(this.loadingObject()));

  /** Whether this reader may submit here: publicly, or for this hub. */
  maySubmit = computed(() => {
    const canCreate = this.page()?.canCreate;

    if (!canCreate) {
      return false;
    }

    return this.hub ? canCreate.hubs.some((hub) => hub.slug === this.hub) : canCreate.public || canCreate.hubs.length > 0;
  });

  /** Every tag name seen, so a chosen tag keeps its name when the other filters stop offering it. */
  private tagNames = new Map<string, string>();

  /** What the list offers, plus any chosen tag no longer offered (count 0), so it can be unticked. */
  tagChoices = computed<FeatureRequestTag[]>(() => {
    const offered = this.page()?.tags ?? [];
    const missing = this.state()
      .tags.filter((slug) => !offered.some((tag) => tag.slug === slug) && this.tagNames.has(slug))
      .map((slug) => ({ slug, name: this.tagNames.get(slug) as string, count: 0 }));

    return [...offered, ...missing];
  });

  /** A short vocabulary is shown whole, as pills; a long one folds into a dropdown checklist. */
  tagsAsPills = computed(() => this.tagChoices().length <= TAG_PILL_LIMIT);

  chosenTagChips = computed(() =>
    this.state().tags.filter((slug) => this.tagNames.has(slug)).map((slug) => ({ slug, name: this.tagNames.get(slug) as string })),
  );

  private load$?: Subscription;

  constructor() {
    effect(() => {
      const state = this.state();
      untracked(() => {
        this.searchText = state.search;
        this.load(state);
      });
    });

    reloadOnLocaleChange(() => this.load(this.state()));
  }

  ngOnDestroy(): void {
    this.load$?.unsubscribe();
  }

  isStageChosen(stage: FeatureRequestStage): boolean {
    return this.state().stages.includes(stage);
  }

  /** Any number on, but never none, so the list is not emptied by accident. */
  toggleStage(stage: FeatureRequestStage): void {
    const chosen = this.state().stages;

    if (this.isAnythingLoading() || (chosen.length === 1 && chosen[0] === stage)) {
      return;
    }

    const next = chosen.includes(stage) ? chosen.filter((s) => s !== stage) : [...chosen, stage];
    this.navigate({ ...this.state(), stages: STAGE_KEYS.filter((key) => next.includes(key)), page: 1 });
  }

  isTagChosen(slug: string): boolean {
    return this.state().tags.includes(slug);
  }

  toggleTag(slug: string): void {
    if (this.isAnythingLoading()) {
      return;
    }

    const chosen = this.state().tags;
    this.navigate({ ...this.state(), tags: chosen.includes(slug) ? chosen.filter((s) => s !== slug) : [...chosen, slug], page: 1 });
  }

  clearTags(): void {
    if (this.state().tags.length && !this.isAnythingLoading()) {
      this.navigate({ ...this.state(), tags: [], page: 1 });
    }
  }

  toggleMine(on: boolean): void {
    this.navigate({ ...this.state(), mine: on, page: 1 });
  }

  toggleFollowing(on: boolean): void {
    this.navigate({ ...this.state(), following: on, page: 1 });
  }

  search(): void {
    const text = this.searchText.trim();

    if (text.length > 0 && text.length < 3) {
      return;
    }

    this.navigate({ ...this.state(), search: text, page: 1 });
  }

  /** An emptied box — by its clear button or by the keyboard — drops the search at once. */
  clearSearchIfEmpty(): void {
    if (this.searchText.trim() === '' && this.state().search) {
      this.search();
    }
  }

  goToPage(page: number): void {
    this.navigate({ ...this.state(), page });
  }

  setLoadingStatus(key: string, value: boolean): void {
    this.loadingObject.update((current) => this.loadingService.setLoadingStatus(key, value, current));
  }

  private load(state: ListState): void {
    this.load$?.unsubscribe();
    this.failure.set(null);
    this.setLoadingStatus('ideas', true);

    const query: FeatureRequestQuery = {
      stages: state.stages,
      tags: state.tags,
      mine: state.mine,
      following: state.following,
      search: state.search,
      page: state.page,
      pageSize: PAGE_SIZE,
      hub: this.hub ?? undefined,
    };

    this.load$ = this.ideas
      .getPage(query)
      .pipe(finalize(() => this.setLoadingStatus('ideas', false)))
      .subscribe({
        next: (page) => {
          page.tags.forEach((tag) => this.tagNames.set(tag.slug, tag.name));
          this.page.set(page);
          this.nameHub(page.hub?.name);

          // A chosen tag nothing can name — retired, or a stale link — is dropped, never shown by slug.
          const nameable = state.tags.filter((slug) => this.tagNames.has(slug));

          if (nameable.length !== state.tags.length) {
            this.navigate({ ...state, tags: nameable }, true);
          }
        },
        error: (failure: FeatureRequestError) => {
          this.page.set(null);
          this.failure.set(failure);
        },
      });
  }

  private navigate(state: ListState, replaceUrl = false): void {
    const isDefault = state.stages.length === DEFAULT_STAGES.length && DEFAULT_STAGES.every((stage) => state.stages.includes(stage));

    this.router.navigate(this.baseRoute, {
      replaceUrl,
      queryParams: {
        stage: isDefault ? null : state.stages.join(','),
        tag: state.tags.length ? state.tags.join(',') : null,
        mine: state.mine ? 'true' : null,
        following: state.following ? 'true' : null,
        search: state.search || null,
        page: state.page > 1 ? state.page : null,
      },
    });
  }

  static read(params: ParamMap): ListState {
    const stages = (params.get('stage') ?? '')
      .split(',')
      .filter((value): value is FeatureRequestStage => (STAGE_KEYS as string[]).includes(value));
    const tags = (params.get('tag') ?? '')
      .split(',')
      .map((slug) => slug.trim())
      .filter((slug) => /^[a-z0-9-]+$/.test(slug));

    return {
      stages: stages.length ? STAGE_KEYS.filter((key) => stages.includes(key)) : [...DEFAULT_STAGES],
      tags: [...new Set(tags)],
      mine: params.get('mine') === 'true',
      following: params.get('following') === 'true',
      page: Math.max(1, Number(params.get('page')) || 1),
      search: params.get('search') ?? '',
    };
  }
}
